import { InfiniteData } from "@tanstack/react-query";
import { UseTellescopeInfiniteQueryOptions, UseTellescopeInfiniteQueryResult } from "../../types";
import { useTellescopeInfiniteQuery } from "../../use-tellescope-infinite-query";
import { TwilioLogsQueryRequest } from "./use-twilio-sms-logs-query";

/** A single Twilio voice call, as returned by the `call-logs` proxy_read endpoint. */
export interface CallLogEntry {
  /** Twilio Call SID */
  sid: string;
  /** Caller phone number (E.164) */
  from: string;
  /** Callee phone number (E.164) */
  to: string;
  /** Twilio call status (completed, busy, no-answer, failed, ...) */
  status: string;
  /** inbound | outbound-api | outbound-dial */
  direction: string;
  /** Call duration in seconds (string, e.g. "42") */
  duration: string;
  /** ISO timestamp (Twilio startTime) */
  startTime: string;
}

export interface TwilioCallLogsResponse {
  data: {
    logs: CallLogEntry[];
    /** null === last page */
    nextPageUrl: string | null;
    hasMore: boolean;
  };
}

/** Result of {@link useTwilioCallLogsQuery}'s `select`: flattened entries plus the oldest timestamp on the last loaded page (used for cross-stream merge watermarking). */
export interface TwilioCallLogsSelection {
  logs: CallLogEntry[];
  /** ms epoch of the oldest entry on the most recently loaded page, or null if no entries loaded. */
  lastPageOldest: number | null;
}

const oldestOfPage = (page: CallLogEntry[]): number | null => {
  if (page.length === 0) return null;
  return page.reduce((min, entry) => Math.min(min, new Date(entry.startTime).getTime()), Infinity);
};

const selectCallLogs = (data: InfiniteData<TwilioCallLogsResponse, string | null>): TwilioCallLogsSelection => ({
  logs: data.pages.flatMap((page) => page.data.logs),
  lastPageOldest: oldestOfPage(data.pages.at(-1)?.data.logs ?? []),
});

export interface UseTwilioCallLogsQueryOptions<TError = Error>
  extends Omit<
    UseTellescopeInfiniteQueryOptions<
      "integrations",
      "proxy_read",
      string | null,
      TError,
      TwilioCallLogsSelection
    >,
    "model" | "method" | "request" | "getNextPageParam" | "initialPageParam" | "paginateRequest" | "select"
  > {
  request?: TwilioLogsQueryRequest;
}

/**
 * Infinite query over Twilio call logs via `integrations.proxy_read` (`type: "call-logs"`).
 *
 * Mirrors {@link useTwilioSmsLogsQuery}: cursor pagination via Twilio's `nextPageUrl` packed into the
 * JSON `query` field, and a `select` output that preserves `lastPageOldest` for cross-stream merging.
 */
export const useTwilioCallLogsQuery = <TError = Error>({
  request,
  ...options
}: UseTwilioCallLogsQueryOptions<TError> = {}): UseTellescopeInfiniteQueryResult<
  "integrations",
  "proxy_read",
  TError,
  TwilioCallLogsSelection
> =>
  useTellescopeInfiniteQuery<"integrations", "proxy_read", string | null, TError, TwilioCallLogsSelection>({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: "Twilio",
      type: "call-logs",
      query: request && Object.values(request).some((v) => v !== undefined) ? JSON.stringify(request) : undefined,
    },
    initialPageParam: "",
    getNextPageParam: (lastPage: TwilioCallLogsResponse) => lastPage.data.nextPageUrl ?? undefined,
    paginateRequest: (cursor, { query, ...rest }) => {
      if (!cursor) return { ...rest, query };
      return { ...rest, query: JSON.stringify({ nextPageUrl: cursor }) };
    },
    select: selectCallLogs,
    ...options,
  });
